import type { FC } from "react";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";

import type { SiteTableEntity } from "src/app/features/Site/types/SiteTableEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { canAccess } from "../../../../core/router/auth/permissions";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { isTheSameByDeepCompare } from "../../../../core/utils/utilFunctions";
import { CSVImportButton } from "../../../FeatureButtons/CSVImportButton";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { useLoadVirtualSiteGroupSitesQuery } from "./vsgServerApi";

const getColumnsConfig = (t: Function): ExtendedColumnsType<SiteTableEntity>[] => {
    return [
        {
            title: t("list.form.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("list.form.name"),
            key: "name",
            dataIndex: "name",
        },
        {
            title: t("oranizationUnitList.siteId"),
            key: "siteId",
            dataIndex: "siteId",
        },
    ];
};

export interface VirtualSitesTableProps extends AntCustomFormField<SiteTableEntity[]>, CommonFieldAttributes {
    id?: string;
}

export const VirtualSitesTable: FC<VirtualSitesTableProps> = ({ value = [], onChange, readOnly, id }) => {
    const hasImportPermission = canAccess("virtual-site-groups", "import");
    const dataGroup = getAssignedDataGroup();
    const navigate = useNavigate();

    const { data: sitesData, refetch } = useLoadVirtualSiteGroupSitesQuery(
        {
            dataGroupCode: dataGroup?.code,
            virtualSiteGroupId: id,
        },
        {
            skip: !id || !dataGroup?.code,
        },
    );

    useEffect(() => {
        if (sitesData?.items && onChange && !isTheSameByDeepCompare(value, sitesData.items)) {
            onChange(sitesData.items);
        }
    }, [sitesData, onChange, value]);

    const handleImportSuccess = useCallback(() => {
        refetch();
    }, [refetch]);

    const onRowClick = (record: SiteTableEntity) => {
        navigate(`${BASE_PATH}/${ResourceName.SITES}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        <PropertyTable<SiteTableEntity>
            name="virtual_sites_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="id"
            tableData={value}
            readOnly={readOnly || false}
            rowsAreSelectable={false}
            onRow={(record: SiteTableEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            customActions={[
                <CSVImportButton
                    key="add-sites"
                    translationKey="button.addSites"
                    resourceName="virtual-site-groups"
                    isDisabled={!hasImportPermission}
                    maxRows={300}
                    id={id}
                    onSuccess={handleImportSuccess}
                />,
                <CSVImportButton
                    key="remove-sites"
                    translationKey="button.removeSites"
                    resourceName="virtual-site-groups"
                    action="remove"
                    isDisabled={!hasImportPermission}
                    maxRows={300}
                    id={id}
                    onSuccess={handleImportSuccess}
                />,
            ]}
        />
    );
};
